/**
 * Make a floating panel movable by dragging its header.
 *
 * The reason is concrete: the change-history slide-over sits over the right
 * of the transaction form, which is exactly where the split table's amounts
 * are — so "View as of here" showed you a past version while covering the
 * numbers you wanted to compare. Rather than guess a layout that never
 * collides, let the panel be moved out of the way.
 *
 * Deliberately small and dependency-free (ADR-0019):
 *
 *  - **Pointer events**, so a trackpad, a mouse and a touch screen are one
 *    code path, with capture so a fast drag that leaves the header does not
 *    drop the panel mid-move.
 *  - **Keyboard too.** A drag handle reachable only by pointer puts the
 *    obscured content out of reach for anyone who cannot use one; arrow keys
 *    move it, and the handle is focusable and labelled.
 *  - **Clamped to the viewport** on every move *and* on resize, because a
 *    panel dragged off-screen cannot be dragged back.
 *  - Nothing is persisted. Where a panel sits is a property of the moment,
 *    not a preference, and restoring a position from last week onto a
 *    different window size is more confusing than starting from the default.
 */

export interface DraggableOptions {
  /** The element that moves. */
  panel: HTMLElement;
  /** The grab area — usually the panel's header. */
  handle: HTMLElement;
  /** Pixels per arrow-key press; Shift multiplies by 5. */
  step?: number;
}

export interface DraggableHandle {
  /** Put the panel back where CSS placed it. */
  reset(): void;
  dispose(): void;
}

/** Keep at least this much of the panel on screen, so there is always something to grab. */
const MIN_VISIBLE = 48;

export function makeDraggable(opts: DraggableOptions): DraggableHandle {
  const { panel, handle } = opts;
  const step = opts.step ?? 24;

  let dx = 0;
  let dy = 0;
  let dragging: { pointerId: number; startX: number; startY: number; originX: number; originY: number } | undefined;

  const apply = (): void => {
    panel.style.transform = dx === 0 && dy === 0 ? '' : `translate(${dx}px, ${dy}px)`;
  };

  /**
   * Clamp against the panel's *laid-out* box (its position minus the offset
   * already applied), so repeated clamping cannot drift.
   */
  const clamp = (nextX: number, nextY: number): { x: number; y: number } => {
    const box = panel.getBoundingClientRect();
    const baseLeft = box.left - dx;
    const baseTop = box.top - dy;

    /*
     * "Never above the top edge" is a promise you can only keep for a panel
     * the window can contain.
     *
     * For a shorter panel it is the right rule: dragging its header off the
     * top would leave nothing to grab and nothing to read. For a taller one
     * it is the opposite of helpful — the top edge is ALREADY above the
     * window, so the rule reads as a demand to move DOWN by however much is
     * hanging off, and the panel leapt on the first arrow key, or through
     * `onResize` on nothing at all. Worse, it made the bottom of a tall form
     * unreachable by dragging, which is the one thing you would drag it for.
     *
     * So a tall panel gets the same symmetric rule the horizontal axis has
     * always had: go where you like, as long as `MIN_VISIBLE` of you stays
     * on screen. Drag up to read the bottom, down to read the top.
     */
    const taller = box.height > window.innerHeight;

    const minX = MIN_VISIBLE - baseLeft - box.width;
    const maxX = window.innerWidth - baseLeft - MIN_VISIBLE;
    const minY = taller
      ? MIN_VISIBLE - baseTop - box.height                   // reach its bottom
      : -baseTop;                                            // never above the top edge
    const maxY = window.innerHeight - baseTop - MIN_VISIBLE;
    return {
      x: Math.min(Math.max(nextX, minX), maxX),
      y: Math.min(Math.max(nextY, minY), maxY),
    };
  };

  const moveBy = (stepX: number, stepY: number): void => {
    const next = clamp(dx + stepX, dy + stepY);
    dx = next.x;
    dy = next.y;
    apply();
  };

  const onPointerDown = (event: PointerEvent): void => {
    // Only a primary press, and never from a control inside the header —
    // otherwise grabbing the panel would swallow its close button.
    if (event.button !== 0) return;
    if ((event.target as HTMLElement).closest('button, a, input, select, textarea')) return;
    dragging = {
      pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, originX: dx, originY: dy,
    };
    handle.setPointerCapture(event.pointerId);
    handle.classList.add('is-dragging');
    event.preventDefault(); // suppress text selection while dragging
  };

  const onPointerMove = (event: PointerEvent): void => {
    if (!dragging || event.pointerId !== dragging.pointerId) return;
    const next = clamp(
      dragging.originX + (event.clientX - dragging.startX),
      dragging.originY + (event.clientY - dragging.startY),
    );
    dx = next.x;
    dy = next.y;
    apply();
  };

  const endDrag = (event: PointerEvent): void => {
    if (!dragging || event.pointerId !== dragging.pointerId) return;
    handle.releasePointerCapture?.(dragging.pointerId);
    handle.classList.remove('is-dragging');
    dragging = undefined;
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    const distance = event.shiftKey ? step * 5 : step;
    switch (event.key) {
      case 'ArrowLeft': moveBy(-distance, 0); break;
      case 'ArrowRight': moveBy(distance, 0); break;
      case 'ArrowUp': moveBy(0, -distance); break;
      case 'ArrowDown': moveBy(0, distance); break;
      case 'Home': dx = 0; dy = 0; apply(); break;  // back to where CSS put it
      default: return;
    }
    event.preventDefault();
  };

  // A window that shrinks must not strand the panel off-screen.
  const onResize = (): void => {
    /*
     * ...but only while the panel is actually laid out. A panel inside a
     * closed dialog has a 0x0 box, and clamping against that invents an
     * offset out of thin air: `minX` becomes `MIN_VISIBLE`, so a panel that
     * had never been moved was shoved 48px sideways. The dialog then opened
     * off-centre, and the only clue was that it took a window resize while
     * it was CLOSED to happen at all.
     */
    const box = panel.getBoundingClientRect();
    if (box.width === 0 && box.height === 0) return;
    moveBy(0, 0);
  };

  handle.addEventListener('pointerdown', onPointerDown);
  handle.addEventListener('pointermove', onPointerMove);
  handle.addEventListener('pointerup', endDrag);
  handle.addEventListener('pointercancel', endDrag);
  handle.addEventListener('keydown', onKeyDown);
  window.addEventListener('resize', onResize);

  handle.classList.add('cf-drag-handle');
  if (!handle.hasAttribute('tabindex')) handle.tabIndex = 0;
  handle.setAttribute('role', 'application');
  handle.setAttribute('aria-label', 'Move this panel — drag, or use the arrow keys');

  return {
    reset(): void { dx = 0; dy = 0; apply(); },
    dispose(): void {
      handle.removeEventListener('pointerdown', onPointerDown);
      handle.removeEventListener('pointermove', onPointerMove);
      handle.removeEventListener('pointerup', endDrag);
      handle.removeEventListener('pointercancel', endDrag);
      handle.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onResize);
      panel.style.transform = '';
    },
  };
}
